/**
 * How an agent tool call is rendered to a human. Shared by the web transcript
 * and by the Linear agent-activity projection in the API, so a Rivets run reads
 * the same wherever it is mirrored.
 */
export type ToolVisualKind = "terminal" | "file" | "edit" | "search" | "web" | "agent" | "generic";

export interface ToolPresentation {
  label: string;
  kind: ToolVisualKind;
  summary: string | null;
  terminalCommand: string | null;
  errorPreview: string | null;
}

const TERMINAL_TOOLS = ["shell", "bash", "terminal", "command", "commandexecution"];
const READ_TOOLS = ["read", "readfile"];
const EDIT_TOOLS = ["write", "writefile", "edit", "multiedit", "applypatch", "patch"];
const SEARCH_TOOLS = ["grep", "glob", "search", "codesearch", "find"];
const WEB_TOOLS = ["websearch", "webfetch", "fetch", "browser"];
/** Tools that hand the work to another agent, which then runs tool calls of its
 * own against the same stream. */
const AGENT_TOOLS = ["task", "agent", "subagent", "dispatchagent"];
/** Tools that report on, or stop, an already-backgrounded shell. They are the
 * only window onto a background task's progress, so they are shown inside it
 * rather than as unrelated rows further down the transcript. */
const BACKGROUND_PROBE_TOOLS = ["bashoutput", "killshell", "killbash", "taskoutput", "taskstop"];

function normalizeToolName(name: string): string {
  return name.toLowerCase().replace(/[._-]/g, "");
}

/** The icon family a tool belongs to. Split out from `presentTool` because the
 * collapsed activity-run row needs to count and glyph a whole run of tools
 * without paying for each one's summary (which stringifies its input). */
export function toolVisualKind(name: string): ToolVisualKind {
  const normalized = normalizeToolName(name);
  if (TERMINAL_TOOLS.includes(normalized)) return "terminal";
  if (READ_TOOLS.includes(normalized)) return "file";
  if (EDIT_TOOLS.includes(normalized)) return "edit";
  if (SEARCH_TOOLS.includes(normalized)) return "search";
  if (WEB_TOOLS.includes(normalized)) return "web";
  if (AGENT_TOOLS.includes(normalized)) return "agent";
  return "generic";
}

export function presentTool(name: string, input: unknown, output: string | undefined, isError: boolean): ToolPresentation {
  const normalized = normalizeToolName(name);
  const values = objectValue(input);
  const kind = toolVisualKind(name);
  let label = humanizeToolName(name);

  if (kind === "terminal") {
    label = "Bash";
  } else if (kind === "file") {
    const explicitLines = numberValue(values?.line_count) ?? numberValue(values?.lineCount);
    const normalizedOutput = output?.replace(/\r?\n$/, "");
    const lineCount = explicitLines ?? (normalizedOutput ? normalizedOutput.split(/\r?\n/).length : undefined);
    label = lineCount && lineCount > 0 ? `Read ${lineCount} lines` : "Read";
  } else if (kind === "edit") {
    label = normalized === "write" || normalized === "writefile" ? "Write" : "Edit";
  } else if (kind === "search") {
    label = normalized === "grep" ? "Grep" : "Search";
  } else if (kind === "web") {
    label = normalized === "websearch" ? "Web Search" : "Web Fetch";
  } else if (kind === "agent") {
    label = "Subagent";
  }

  return {
    label,
    kind,
    summary: summarizeToolInput(input),
    terminalCommand: kind === "terminal" ? terminalCommand(input) : null,
    errorPreview: isError ? firstUsefulLine(output) : null,
  };
}

/**
 * Work the agent handed off rather than did: a sub-agent, or a tool it left
 * running in the background.
 *
 * Both are invisible in a plain tool row, and a backgrounded one is actively
 * misleading — its result arrives the instant it is launched, so the row reads
 * as finished while the work it started has not begun. Naming the hand-off is
 * what lets a client draw it as a container for the progress that follows.
 */
export type DelegationKind = "subagent" | "background";

export interface ToolDelegation {
  kind: DelegationKind;
  /** True when the call returned before the work did. Such a row must not be
   * drawn as complete when its own result lands. */
  background: boolean;
  /** Row title: "Subagent", "Background subagent", "Background task". */
  label: string;
  /** The agent type, description or command — what this hand-off is for. */
  detail: string | null;
}

/** Whether a tool input asks for the work to continue after the call returns. */
export function isBackgroundRequest(input: unknown): boolean {
  const values = objectValue(input);
  if (!values) return false;
  return values.run_in_background === true || values.runInBackground === true;
}

/** Whether a tool exists only to report on work already running elsewhere. */
export function isBackgroundProbeTool(name: string): boolean {
  return BACKGROUND_PROBE_TOOLS.includes(normalizeToolName(name));
}

/** `null` for an ordinary tool call — the overwhelming majority. */
export function toolDelegation(name: string, input: unknown): ToolDelegation | null {
  const values = objectValue(input);
  const background = isBackgroundRequest(input);
  if (toolVisualKind(name) === "agent") {
    const type = stringValue(values?.subagent_type) ?? stringValue(values?.subagentType);
    const description = stringValue(values?.description);
    return {
      kind: "subagent",
      background,
      label: background ? "Background subagent" : "Subagent",
      detail: [type, description].filter(Boolean).join(" · ") || summarizeToolInput(input),
    };
  }
  if (!background) return null;
  return {
    kind: "background",
    background: true,
    label: "Background task",
    detail: summarizeToolInput(input),
  };
}

export function summarizeReasoning(text: string, maxLength = 110): string {
  return truncate(compact(text), maxLength);
}

/** Keys whose value is the command line, in the order providers prefer them. */
const COMMAND_KEYS = ["command", "cmd", "script", "shell_command", "shellCommand"] as const;
/** Keys whose value is a single path. Rendered path-shortened, never as JSON. */
const PATH_KEYS = ["path", "file_path", "filePath", "notebook_path", "notebookPath", "target_file", "targetFile"] as const;
/** Everything else worth showing verbatim before falling back to JSON. */
const TEXT_KEYS = ["query", "pattern", "url", "prompt", "description"] as const;

/** The one line that describes what a tool call is doing. `maxLength` exists
 * because the Linear activity feed can afford a far longer parameter than a
 * transcript row. */
export function summarizeToolInput(input: unknown, maxLength = SUMMARY_LENGTH): string | null {
  if (input == null) return null;
  if (typeof input === "string") return truncate(shortenWorkspacePaths(compact(stripRedundantCd(input))), maxLength);
  const object = objectValue(input);
  if (object) {
    // A command is the most specific thing a tool input can carry, so it wins
    // over any incidental path field sitting beside it.
    const command = commandText(object);
    if (command !== null) return truncate(shortenWorkspacePaths(compact(command)), maxLength);
    const pathKey = PATH_KEYS.find((candidate) => typeof object[candidate] === "string");
    if (pathKey) return truncate(shortenPath(compact(String(object[pathKey]))), maxLength);
    const textKey = TEXT_KEYS.find((candidate) => typeof object[candidate] === "string");
    if (textKey) return truncate(shortenWorkspacePaths(compact(String(object[textKey]))), maxLength);
    const files = arrayValue(object.files);
    if (files) {
      const paths = files.map((file) => {
        if (typeof file === "string") return file;
        const item = objectValue(file);
        return typeof item?.path === "string" ? item.path : null;
      }).filter((path): path is string => Boolean(path)).map(shortenPath);
      if (paths.length > 0) return paths.length === 1 ? paths[0]! : `${paths[0]} +${paths.length - 1}`;
    }
  }
  try {
    return truncate(shortenWorkspacePaths(compact(JSON.stringify(input))), maxLength);
  } catch {
    return null;
  }
}

function terminalCommand(input: unknown): string | null {
  if (typeof input === "string") return shortenWorkspacePaths(stripRedundantCd(input));
  const object = objectValue(input);
  if (!object) return null;
  const command = commandText(object);
  return command === null ? null : shortenWorkspacePaths(command);
}

/** Shell wrappers an agent puts in front of the script it actually ran. */
const SHELL_BINARIES = new Set(["bash", "sh", "zsh", "dash", "fish", "/bin/bash", "/bin/sh", "/bin/zsh", "/usr/bin/bash", "/usr/bin/sh", "/usr/bin/zsh"]);
const SHELL_FLAGS = new Set(["-c", "-lc", "-cl", "-ic", "-lic", "--command"]);

/**
 * The command a tool input describes, as the text a person would have typed.
 *
 * Codex sends `command` as argv (`["bash","-lc","printf hi > f.txt"]`), which
 * used to miss the string-only check below and fall through to the raw-JSON
 * fallback — so the transcript showed the wire format instead of the command.
 * Returns `null` (not `""`) when there is no command, so an empty argv array is
 * distinguishable from an absent field.
 */
function commandText(object: Record<string, unknown>): string | null {
  for (const key of COMMAND_KEYS) {
    const value = object[key];
    if (typeof value === "string") return stripRedundantCd(value);
    const argv = arrayValue(value);
    if (!argv) continue;
    const parts = argv.filter((part): part is string => typeof part === "string");
    if (parts.length !== argv.length) continue; // not an argv array — let JSON win
    // `bash -lc "<script>"` is a wrapper, not the command: show the script.
    if (parts.length >= 3 && SHELL_BINARIES.has(parts[0]!.toLowerCase()) && SHELL_FLAGS.has(parts[1]!.toLowerCase())) {
      return stripRedundantCd(parts.slice(2).join(" "));
    }
    return stripRedundantCd(parts.join(" "));
  }
  return null;
}

/** Worktree checkouts live under `<rivets home>/worktrees/<id>/`, optionally
 * nested under a per-worker directory (`…/desktop/wkr_x/worktrees/wt_y/`).
 * Agents echo those absolute roots back in tool inputs, which tells the reader
 * nothing — only the path inside the worktree is meaningful. */
// Both patterns only match at a token boundary so a relative path that merely
// contains these segments (`apps/worktrees/x`) is left alone.
const WORKTREE_ROOT = /(^|[\s"'`(=:,])(?:\/[^/\s"'`]+)*\/worktrees\/[^/\s"'`]+\//g;
/** Anything else absolute under a home directory still reads better as `~/…`. */
const HOME_ROOT = /(^|[\s"'`(=:,])(?:\/Users|\/home)\/[^/\s"'`]+\//g;
/** Agents habitually prefix every shell call with a `cd` into the worktree they
 * were already started in. The reader gains nothing from it, and it pushes the
 * real command off the end of a one-line summary. */
const LEADING_WORKTREE_CD = /^\s*cd\s+(["'`]?)(?:\/[^/\s"'`]+)*\/worktrees\/[^/\s"'`]+\/?\1\s*(?:&&|;|\n)\s*/;

/** Default one-line budget for a transcript row. */
const SUMMARY_LENGTH = 180;

function stripRedundantCd(command: string): string {
  const stripped = command.replace(LEADING_WORKTREE_CD, "");
  // A bare `cd <worktree>` with nothing after it is the whole command; keep it.
  return stripped.trim() ? stripped : command;
}

/** Drop the workspace root from a single path, leaving the worktree-relative
 * portion (the only part the user can act on). */
export function shortenPath(value: string): string {
  const shortened = shortenWorkspacePaths(value.trim());
  return shortened || ".";
}

/** Same, for free-form text (shell commands) that may embed several paths. */
function shortenWorkspacePaths(value: string): string {
  return value
    .replace(WORKTREE_ROOT, "$1")
    .replace(HOME_ROOT, "$1~/");
}

function humanizeToolName(name: string): string {
  const spaced = name
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .trim();
  if (!spaced) return "Tool";
  return spaced.replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function firstUsefulLine(output: string | undefined): string | null {
  if (!output) return null;
  const line = output.split(/\r?\n/).map((value) => value.trim()).find(Boolean);
  return line ? truncate(line, 180) : null;
}

function compact(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}

function truncate(value: string, maxLength: number): string {
  return value.length <= maxLength ? value : `${value.slice(0, maxLength - 1)}…`;
}

function objectValue(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value) ? value as Record<string, unknown> : null;
}

function arrayValue(value: unknown): unknown[] | null {
  return Array.isArray(value) ? value : null;
}

function stringValue(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function numberValue(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? Math.max(0, Math.round(value)) : undefined;
}
